import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { SavedReportView, SavedReportViewReportType } from '../../../../../../models/saved-report-view';
import { SavedReportViewService } from '../../../../../../services/saved-report-view/saved-report-view.service';
import { AccountService } from '../../../../../../services/account/account.service';
import { MyAccount } from '../../../../../../models/my-account';
import { dateStringFromISO8601, getStaffNameDisplay } from '../../../../../../utils';


// Whether the modal configures the current user's personal default view or the
// organization-wide default view (PRO-1243). The two share an identical UI and
// differ only in their data source, labels, and which endpoint they call.
export type DefaultReportViewScope = 'personal' | 'organization';


@Component({
  selector: 'app-set-default-report-view-modal',
  templateUrl: './set-default-report-view-modal.component.html',
  styleUrls: ['./set-default-report-view-modal.component.css']
})
export class SetDefaultReportViewModalComponent implements OnInit {
  @Input() reportType: SavedReportViewReportType;
  @Input() scope: DefaultReportViewScope = 'personal';

  public srvs: SavedReportView[];
  public loadingSrvs = true;

  // The ID of the saved view currently set as the default for this scope, or
  // undefined if there is no default.
  public currentDefaultViewId: number | undefined;

  public loadingSubmit = false;

  public successMessage = '';
  public errorMessage = '';
  public serverError: number = null;

  public readonly getStaffNameDisplay = getStaffNameDisplay;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public activeModal: NgbActiveModal,
    private savedReportViewService: SavedReportViewService,
    private accountService: AccountService,
  ) {}

  ngOnInit() {
    this.currentDefaultViewId = this.readDefaultViewId(
      this.accountService.getMyAccount()
    );
    this.loadSrvs();
  }

  public get isOrganizationScope(): boolean {
    return this.scope === 'organization';
  }

  public get headerText(): string {
    return this.isOrganizationScope
      ? 'Set Organization Default View'
      : 'Set Personal Default View';
  }

  public get introText(): string {
    return this.isOrganizationScope
      ? 'Select the view that should be shown by default for everyone in your ' +
        'organization. Staff who have chosen their own default view will ' +
        'continue to see that instead.'
      : 'Select the view that should be shown by default whenever you open ' +
        'this report.';
  }

  public get noDefaultLabel(): string {
    return this.isOrganizationScope
      ? 'No Organization Default'
      : 'No Personal Default';
  }

  private readDefaultViewId(myAccount: MyAccount): number | undefined {
    return this.isOrganizationScope
      ? myAccount.organization_default_report_view_ids?.[this.reportType]
      : myAccount.default_report_view_ids?.[this.reportType];
  }

  private loadSrvs() {
    this.loadingSrvs = true;
    this.savedReportViewService
      .getSavedReportViews(1, 100, {report_type: this.reportType})
      .then(result => {
        this.srvs = result.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingSrvs = false; });
  }

  /**
   * Set the default view for this scope to the given saved view, or clear it
   * when viewId is 0 (the "No ... Default" option).
   */
  public setDefault(viewId: number) {
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request = this.isOrganizationScope
      ? this.accountService.setOrganizationDefaultReportViewId(this.reportType, viewId)
      : this.accountService.setDefaultReportViewId(this.reportType, viewId);

    request
      .then(updatedMyAccount => {
        this.currentDefaultViewId = this.readDefaultViewId(updatedMyAccount);
        this.successMessage = this.isOrganizationScope
          ? 'Organization default view updated.'
          : 'Personal default view updated.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          reason.error.text().then(text => { this.errorMessage = text; });
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to set this default view.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }
}
